import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Alert, Spinner, ProgressBar } from "react-bootstrap";
import { searchUploadAccounts, uploadVideoForAccount } from "../../apis/adminVideoUpload";

const prepareVideo = (file) => new Promise((resolve, reject) => {
  const video = document.createElement("video");
  const url = URL.createObjectURL(file);
  let finished = false;
  const finish = (result, error) => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    video.onloadedmetadata = null;
    video.onseeked = null;
    video.onerror = null;
    video.removeAttribute("src");
    video.load();
    URL.revokeObjectURL(url);
    error ? reject(error) : resolve(result);
  };
  const timer = setTimeout(() => finish(null, new Error("Unable to read this video. Try an MP4 file.")), 20000);
  video.preload = "auto";
  video.muted = true;
  video.onloadedmetadata = () => {
    if (!Number.isFinite(video.duration) || video.duration <= 0) {
      finish(null, new Error("Unable to read video duration."));
      return;
    }
    video.currentTime = Math.min(0.2, video.duration / 2);
  };
  video.onseeked = () => {
    const duration = video.duration;
    const canvas = document.createElement("canvas");
    canvas.width = Math.min(video.videoWidth, 720);
    canvas.height = Math.round(canvas.width * video.videoHeight / video.videoWidth);
    try {
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((cover) => cover
        ? finish({ duration, cover })
        : finish(null, new Error("Unable to create the video cover.")), "image/jpeg", 0.9);
    } catch { finish(null, new Error("Unable to create the video cover.")); }
  };
  video.onerror = () => finish(null, new Error("This video cannot be read. Try an MP4 file."));
  video.src = url;
});

export default function AdminVideoUploadModal({ onClose, onUploaded, initialAccount }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [accounts, setAccounts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [account, setAccount] = useState(initialAccount || null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [prepared, setPrepared] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [privacy, setPrivacy] = useState("false");
  const [error, setError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const preparation = useRef(0);

  useEffect(() => {
    if (initialAccount) return;
    const controller = new AbortController();
    setSearching(true);
    setSearchError("");
    const timer = setTimeout(() => {
      searchUploadAccounts(search, page, controller.signal).then((data) => {
        if (controller.signal.aborted) return;
        setAccounts(data.payload || []);
        setHasMore(Boolean(data.pagination?.hasMore));
      }).catch((err) => {
        if (!controller.signal.aborted) { setAccounts([]); setSearchError(err.response?.data?.message || "Unable to load accounts."); }
      }).finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [search, page, initialAccount]);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => () => { preparation.current += 1; }, []);

  const chooseFile = async (event) => {
    const next = event.target.files[0];
    const version = ++preparation.current;
    setFile(next || null); setPrepared(null); setError(""); setPreview("");
    setPreparing(Boolean(next));
    if (!next) return;
    try {
      const result = await prepareVideo(next);
      if (preparation.current === version) setPrepared(result);
    } catch (err) { if (preparation.current === version) setError(err.message); }
    finally { if (preparation.current === version) setPreparing(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (uploading) return;
    if (!account || !file || !prepared) { setError("Select an account and a video first."); return; }
    if (title.trim().length < 10 || caption.trim().length < 20) {
      setError("Title needs at least 10 characters, description at least 20."); return;
    }
    setUploading(true); setProgress(0); setError("");
    const form = new FormData();
    form.append("video", file);
    form.append("cover", prepared.cover, "cover.jpg");
    form.append("title", title.trim());
    form.append("caption", caption.trim());
    form.append("privacy", privacy);
    form.append("video_durations", String(prepared.duration));
    form.append("allow_comment", "true");
    form.append("allow_duet", "true");
    form.append("allow_stitch", "true");
    try {
      const result = await uploadVideoForAccount(account.id, form, (event) => {
        if (event.total) setProgress(Math.round(event.loaded * 100 / event.total));
      });
      onUploaded(result.payload, account);
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed. Check the account's video list before retrying.");
      setUploading(false);
    }
  };

  return (
    <Modal show onHide={uploading ? () => {} : onClose} backdrop="static" keyboard={!uploading} size="lg" centered scrollable>
      <Modal.Header closeButton={!uploading}><Modal.Title>Upload video to an account</Modal.Title></Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <fieldset disabled={uploading}>
            {!initialAccount && <>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="upload-account-search">Choose account</Form.Label>
              <Form.Control id="upload-account-search" placeholder="Search by ID, username or nickname" value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(1); }} />
            </Form.Group>
            {searchError && <Alert variant="danger">{searchError}</Alert>}
            <div className="border rounded mb-2" style={{ maxHeight: 180, overflowY: "auto" }} aria-label="Accounts">
              {searching ? <p className="p-3 mb-0" role="status">Loading accounts…</p> : accounts.length ? accounts.map((user) => (
                <button key={user.id} type="button" className={`list-group-item list-group-item-action ${account?.id === user.id ? "active" : ""}`}
                  aria-pressed={account?.id === user.id} onClick={() => setAccount(user)}>
                  <strong>{user.nickname || user.username}</strong> · @{user.username} · ID {user.id}
                </button>
              )) : <p className="p-3 mb-0">No accounts found.</p>}
            </div>
            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" size="sm" disabled={searching || page === 1} onClick={() => setPage(page - 1)}>Previous</Button>
              <span className="align-self-center">Page {page}</span>
              <Button variant="outline-secondary" size="sm" disabled={searching || !hasMore} onClick={() => setPage(page + 1)}>Next</Button>
            </div>
            </>}
            {account && <Alert variant="info">Posting as <strong>@{account.username}</strong> (ID {account.id}).</Alert>}
            <Form.Group className="mb-3">
              <Form.Label htmlFor="admin-upload-video">Video</Form.Label>
              <Form.Control id="admin-upload-video" type="file" accept="video/mp4,video/quicktime,video/x-matroska" onChange={chooseFile} />
              <Form.Text>MP4, MOV or MKV. A cover image is generated automatically.</Form.Text>
            </Form.Group>
            {preparing && <p role="status"><Spinner size="sm" animation="border" /> Preparing video cover…</p>}
            {preview && <video src={preview} controls preload="metadata" style={{ width: "100%", maxHeight: 250, background: "#111" }} />}
            <Form.Group className="my-3">
              <Form.Label htmlFor="admin-upload-title">Title</Form.Label>
              <Form.Control id="admin-upload-title" required minLength={10} value={title} onChange={(event) => setTitle(event.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="admin-upload-caption">Description</Form.Label>
              <Form.Control id="admin-upload-caption" as="textarea" rows={3} required minLength={20} value={caption} onChange={(event) => setCaption(event.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="admin-upload-privacy">Visibility</Form.Label>
              <Form.Select id="admin-upload-privacy" value={privacy} onChange={(event) => setPrivacy(event.target.value)}>
                <option value="false">Public</option><option value="true">Private</option>
              </Form.Select>
            </Form.Group>
          </fieldset>
          {error && <Alert variant="danger" className="mt-3" role="alert">{error}</Alert>}
          {uploading && <div className="mt-3" role="status"><ProgressBar now={progress} label={`${progress}%`} />
            <p className="mt-2 mb-0">{progress === 100 ? "Saving video…" : "Uploading video…"}</p></div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={uploading} onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={uploading || preparing || !account || !prepared}>{uploading ? "Uploading…" : "Upload video"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
